const Sentry = require('@sentry/browser');
const { mainConfigService } = require('./services/config');
const { WEB_SDK_CHANNEL } = require('./constants');

// Header generation helpers
const createAuthHeaders = (apiKey) => ({
  'Authorization': `Api-Key ${apiKey}`,
  'Accept-Language': mainConfigService.get('lang')
});

const createJsonHeaders = (apiKey) => ({
  ...createAuthHeaders(apiKey),
  'Accept': 'application/json',
  'Content-Type': 'application/json'
});

const createOttuPGHeaders = (apiKey, merchant_id) => ({
  ...createJsonHeaders(apiKey),
  'Channel': WEB_SDK_CHANNEL,
  'Merchant-Id': merchant_id
});

// Standardized response handler
const handleApiResponse = (response) => {
  if (response.ok) {
    return response.json();
  } else {
    if (response.status >= 500) {
      throw response;
    } else {
      throw response.json();
    }
  }
};

// Simplified API call wrapper
const makeApiCall = async (url, options, excludeFromLogging = []) => {
  const response = await sdkFetch(url, options, excludeFromLogging);
  return handleApiResponse(response);
};

// Every SDK API call targets an absolute https URL. A missing or relative URL
// (e.g. an unresolved submit_url) makes window.fetch resolve it against the
// current page, silently sending the request — and any payment payload — to
// window.location instead of the intended endpoint. Report it so this class of
// bug is caught instead of failing silently.
const isAbsoluteUrl = (url) =>
  typeof url === 'string' && /^https?:\/\//i.test(url.trim());

const reportRequestToWindowLocation = (url, options) => {
  if (isAbsoluteUrl(url)) return;

  try {
    const providedUrl = typeof url === 'string' ? url : String(url);
    let resolvedTo = window.location.href;
    try {
      resolvedTo = new URL(providedUrl, window.location.href).href;
    } catch {
      // keep the page URL as the resolved target
    }

    Sentry.withScope((scope) => {
      scope.setTag('sdk_error', 'request_to_window_location');
      scope.setExtras({
        request_method: options?.method || 'GET',
        provided_url: providedUrl,
        resolved_to: resolvedTo,
        window_location: window.location.href,
      });
      Sentry.captureException(
        new Error(
          'SDK API request has a missing or relative URL and would be sent to window.location'
        )
      );
    });
  } catch (e) {
    // Diagnostics must never interfere with the request itself.
  }
};

const sdkFetch = async (url, options, excludeFromLogging = []) => {
  reportRequestToWindowLocation(url, options);
  return await window.fetch(url, options)
    .then((response) => {
      const shouldLog =
        (response.status > 400 ||
          (response.status >= 400 && mainConfigService.get('captureAll'))) &&
        !excludeFromLogging.includes(response.status);
      
      if (shouldLog) {
        Sentry.captureMessage(`Server error response status ${response.status}`);
      }
      return response;
    })
    .catch((error) => {
      Sentry.captureException(error);
      throw error;
    });
};
exports.sdkFetch = sdkFetch;

const fetchSentryConfig = async (merchant_id, apiKey) => {
  let url = process.env.SDKSTUDIO_URL || 'https://studiosdk.ottu.net/config';
  try {
    const response = await sdkFetch(url, {
      method: 'POST',
      headers: createAuthHeaders(apiKey),
      body: JSON.stringify({
        'merchant_id': merchant_id,
        'platform': 'web'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch Sentry config');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};
exports.fetchSentryConfig = fetchSentryConfig;

const getPaymentDetails = async (merchant_id, session_id, apiKey) => {
  const url = `https://${merchant_id}/b/checkout/api/sdk/v1/pymt-txn/submit/${session_id}`;
  
  return makeApiCall(url, {
    method: 'GET',
    headers: createAuthHeaders(apiKey),
  });
};
exports.getPaymentDetails = getPaymentDetails;

const getCheckoutPageStatus = async (merchant_id, session_id, apiKey) => {
  const url = `https://${merchant_id}/b/checkout/api/intl/v1/checkout-page/${session_id}`;

  return makeApiCall(url, {
    method: 'GET',
    headers: createAuthHeaders(apiKey),
  });
};
exports.getCheckoutPageStatus = getCheckoutPageStatus;

const redirectPayFetch = async (submit_url, apiKey, code, save_card) => {
  return makeApiCall(submit_url, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({
      pg_code: code,
      save_card: save_card
    })
  });
};
exports.redirectPayFetch = redirectPayFetch;

const redirectPayFetchForAba = async (submit_url, session_id, sub_option_value, apiKey, code) => {
  return makeApiCall(submit_url, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({
      session_id: session_id,
      sub_option: sub_option_value,
      pg_code: code
    })
  });
};
exports.redirectPayFetchForAba = redirectPayFetchForAba;

const sendMobilePayOtp = async (submit_url, apiKey, session_id, code, customer_phone, save_card) => {
  return makeApiCall(submit_url, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({
      customer_phone: customer_phone,
      session_id: session_id,
      pg_code: code,
      save_card: save_card,
    }),
  });
};
exports.sendMobilePayOtp = sendMobilePayOtp;

const submitMobilePayOTP = async (submit_url, apiKey, session_id, otp) => {
  return makeApiCall(submit_url, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({
      session_id: session_id,
      otp: otp,
    }),
  });
};
exports.submitMobilePayOTP = submitMobilePayOTP;

const tokenPayFetch = async (submit_url, apiKey, merchant_id, session_id, code, trandata, enc, isOttuPg, cvv) => {
  const body = isOttuPg
    ? { trandata, enc }
    : {
        session_id,
        token: code,
        ...(cvv && { cvv }),
      };
  
  return makeApiCall(submit_url, {
    method: 'POST',
    headers: createOttuPGHeaders(apiKey, merchant_id),
    body: JSON.stringify(body),
  });
};
exports.tokenPayFetch = tokenPayFetch;

const deleteTokenFetch = async (submit_url, apiKey) => {
  const response = await sdkFetch(submit_url, {
    method: 'DELETE',
    headers: createAuthHeaders(apiKey)
  });
  
  if (response.ok) {
    return response;
  } else {
    if (response.status >= 500) {
      throw response;
    } else {
      throw response.json();
    }
  }
};
exports.deleteTokenFetch = deleteTokenFetch;

const updateTxnStatus = async (merchant_id, session_id, apiKey) => {
  const url = `https://${merchant_id}/b/checkout/api/sdk/v1/concur-payer-view/${session_id}`;
  
  const response = await sdkFetch(url, {
    method: 'GET',
    headers: createAuthHeaders(apiKey)
  }, [404]); // Exclude 404s from Sentry logging as they're expected for this API
  
  if (response.ok) {
    return response.json();
  } else {
    if (response.status >= 500) {
      throw response;
    } else if (response.status !== 404) {
      throw response.json();
    }
  }
};
exports.updateTxnStatus = updateTxnStatus;

const fetchInstallmentOptions = async (url, apiKey, code) => {
  return makeApiCall(url, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({
      pg_code: code,
    })
  });
};
exports.fetchInstallmentOptions = fetchInstallmentOptions;

const cancelPayFetch = async (url, apiKey) => {
  return makeApiCall(url, {
    method: 'POST',
    headers: createJsonHeaders(apiKey)
  });
};
exports.cancelPayFetch = cancelPayFetch;

const walletReserveFetch = async (reserveUrl, apiKey, code, sessionId, currency) => {
  return makeApiCall(reserveUrl, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({ code, session_id: sessionId, currency }),
  });
};
exports.walletReserveFetch = walletReserveFetch;

const walletSubmitFetch = async (submitUrl, apiKey, code, sessionId) => {
  return makeApiCall(submitUrl, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({ code, session_id: sessionId }),
  });
};
exports.walletSubmitFetch = walletSubmitFetch;

const walletCancelFetch = async (cancelUrl, apiKey, code, sessionId) => {
  return makeApiCall(cancelUrl, {
    method: 'POST',
    headers: createJsonHeaders(apiKey),
    body: JSON.stringify({ code, session_id: sessionId }),
  });
};
exports.walletCancelFetch = walletCancelFetch;


const ctpSumbitFetch = (submit_url, method, completeMandateResponse) => {
  const body = {
    funding_source: completeMandateResponse,
  };
  return makeApiCall(submit_url, {
    method: 'POST',
    headers: createJsonHeaders(mainConfigService.get('apiKey')),
    body: JSON.stringify(body),
  });
};
exports.ctpSumbitFetch = ctpSumbitFetch;

const fetchEncKey = async (merchant_id, apiKey, url) => {
  return makeApiCall(url, {
    method: 'GET',
    headers: createOttuPGHeaders(apiKey, merchant_id)
  });
};
exports.fetchEncKey = fetchEncKey;
